import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { getPurchaseProvider, canOfferPurchase, getPurchaseKind } from '../../services/purchase/purchaseProvider.js';
import { apiClient } from '../../services/http.js';
import { CheckoutWidget } from './CheckoutWidget.jsx';
import { PlanChangeDialog } from './PlanChangeDialog.jsx';
import { PlanCard } from './PlanCard.jsx';
import { NativeSubscriptionControls } from './NativeSubscriptionControls.jsx';

export function PlanGrid({ plans, currentPlanId }) {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    // App Store Guideline 3.1.1: no build nativo o app não pode oferecer
    // compra fora do In-App Purchase. Enquanto o RevenueCat não estiver
    // configurado, os planos aparecem só como informação, sem CTA de compra.
    const purchaseEnabled = canOfferPurchase();
    const purchaseKind = getPurchaseKind();
    const [billingPeriod, setBillingPeriod] = useState('quarterly'); // 'quarterly' ou 'annual'
    const [loading, setLoading] = useState(false);
    const [checkoutSecret, setCheckoutSecret] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [pendingChange, setPendingChange] = useState(null); // { toPlan, isDowngrade } | null
    // Preços vindos da loja, indexados por `${planId}:${period}`. No nativo a
    // Apple exige exibir o preço que ELA informa (localizado e na moeda do
    // usuário) — mostrar um valor diferente do cobrado é causa de rejeição.
    const [storePrices, setStorePrices] = useState(null);

    const paidPlans = plans.filter((p) => !p.is_trial);
    const currentPlan = plans.find((p) => p.plan_id === (currentPlanId || 'trial')) || null;

    useEffect(() => {
        if (purchaseKind !== 'revenuecat' || !purchaseEnabled) return;
        let cancelled = false;

        (async () => {
            try {
                const provider = await getPurchaseProvider();
                const offerings = await provider.listOfferings({ appUserId: user?.id });
                if (cancelled) return;
                const byKey = {};
                for (const offer of offerings) {
                    byKey[`${offer.planId}:${offer.period}`] = offer.priceLabel;
                }
                setStorePrices(byKey);
            } catch (err) {
                // Sem preço da loja não exibimos preço nenhum — melhor faltar
                // informação do que exibir um valor que não é o cobrado.
                if (!cancelled) setStorePrices({});
                console.error(err);
            }
        })();

        return () => { cancelled = true; };
    }, [purchaseKind, purchaseEnabled, user?.id]);

    const handleSelectPlan = async (plan) => {
        if (!isAuthenticated) {
            navigate('/register');
            return;
        }

        setLoading(true);
        setError(null);
        setNotice(null);

        try {
            const provider = await getPurchaseProvider();
            const result = await provider.purchase(plan, {
                billingCycle: billingPeriod,
                appUserId: user?.id,
            });

            // Estado 1 — desistência: o usuário fechou a folha de pagamento da
            // loja. Não é falha; sai em silêncio, sem toast vermelho.
            if (result?.cancelled) {
                return;
            }

            // Estado 2 — Stripe (web): devolve o client_secret para montar o
            // checkout embedded; a compra ainda não aconteceu.
            if (result?.clientSecret) {
                setCheckoutSecret(result.clientSecret);
                return;
            }

            // Estado 3 — compra concluída na loja. Sincroniza no backend antes
            // de mostrar o resultado: sem isso o usuário pagaria e continuaria
            // vendo o plano antigo até o webhook chegar.
            if (result?.success) {
                const { res } = await apiClient.request('/billing/native/sync', { method: 'POST' });
                if (!res.ok) {
                    // O pagamento existe; o que falhou foi só refletir aqui. O
                    // webhook ainda vai chegar, então não é erro do usuário.
                    setNotice('Compra concluída. Estamos liberando seu plano — pode levar alguns instantes.');
                    return;
                }
                navigate('/billing/return');
            }
        } catch (err) {
            setError(err?.message || 'Não foi possível concluir a compra. Tente novamente.');
            console.error(err);
        } finally {
            setLoading(false);
            setPendingChange(null);
        }
    };

    const handlePlanClick = (plan, isDowngrade) => {
        if (isAuthenticated && currentPlan && currentPlan.plan_id !== plan.plan_id) {
            setPendingChange({ toPlan: plan, isDowngrade });
            return;
        }
        handleSelectPlan(plan);
    };

    const handleCloseCheckout = () => {
        setCheckoutSecret(null);
    };

    return (
        <div className="flex flex-col items-center w-full">
            <div className="flex flex-col items-center gap-3 mb-12">
                <div className="inline-flex bg-surface-container rounded-full p-1 border border-outline-variant/20 shadow-inner">
                    <button
                        onClick={() => setBillingPeriod('quarterly')}
                        className={`px-6 py-2 rounded-full text-xs font-bold transition-all ${
                            billingPeriod === 'quarterly'
                                ? 'bg-primary text-on-primary shadow-md'
                                : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                    >
                        Trimestral
                    </button>
                    <button
                        onClick={() => setBillingPeriod('annual')}
                        className={`px-6 py-2 rounded-full text-xs font-bold transition-all ${
                            billingPeriod === 'annual'
                                ? 'bg-primary text-on-primary shadow-md'
                                : 'text-on-surface-variant hover:text-on-surface'
                        }`}
                    >
                        Anual <span className="ml-1 text-[9px] bg-primary-container text-on-primary-container px-1.5 py-0.5 rounded-full">Economize</span>
                    </button>
                </div>
                {/* A menção ao Stripe (processador externo) não pode aparecer
                    no build nativo — Guideline 3.1.1 proíbe direcionar o
                    usuário a pagamento fora do In-App Purchase. */}
                {purchaseKind === 'stripe' && (
                    <p className="text-xs text-on-surface-variant/50">Cobrança única e recorrente pelo Stripe.</p>
                )}
            </div>

            {error && (
                <p className="text-center text-error text-sm font-semibold max-w-md mx-auto mb-6">{error}</p>
            )}
            {notice && (
                <p role="status" className="text-center text-primary text-sm font-semibold max-w-md mx-auto mb-6">{notice}</p>
            )}

            <div className="grid md:grid-cols-3 gap-8 items-stretch w-full">
                {paidPlans.map((plan) => {
                    const isCurrent = plan.plan_id === currentPlanId;
                    const isUpgrade = !isCurrent && currentPlan != null && plan.rank > currentPlan.rank;
                    const isDowngrade = !isCurrent && currentPlan != null && plan.rank < currentPlan.rank;

                    return (
                        <PlanCard
                            key={plan.plan_id}
                            plan={plan}
                            billingPeriod={billingPeriod}
                            isCurrent={isCurrent}
                            isUpgrade={isUpgrade}
                            isDowngrade={isDowngrade}
                            popular={plan.plan_id === 'regular'}
                            loading={loading}
                            notAuthenticated={!isAuthenticated}
                            purchaseEnabled={purchaseEnabled}
                            storePriceLabel={storePrices?.[`${plan.plan_id}:${billingPeriod}`]}
                            requireStorePrice={purchaseKind === 'revenuecat'}
                            onSelect={handlePlanClick}
                        />
                    );
                })}
            </div>

            {/* Obrigatórios da App Store numa tela que vende assinatura. */}
            {purchaseKind === 'revenuecat' && (
                <NativeSubscriptionControls
                    appUserId={user?.id}
                    onRestored={() => navigate('/billing/return')}
                />
            )}

            {pendingChange && (
                <PlanChangeDialog
                    fromPlan={currentPlan}
                    toPlan={pendingChange.toPlan}
                    isDowngrade={pendingChange.isDowngrade}
                    loading={loading}
                    onConfirm={() => handleSelectPlan(pendingChange.toPlan)}
                    onClose={() => setPendingChange(null)}
                />
            )}

            {checkoutSecret && (
                <CheckoutWidget
                    clientSecret={checkoutSecret}
                    onClose={handleCloseCheckout}
                />
            )}
        </div>
    );
}
